export function createInstrumentCascadeProps({ lazy, lazyLoad }) {
  return {
    multiple: true,
    //expandTrigger: 'hover',
    label: 'label',
    value: 'value',
    children: 'children',
    lazy,
    lazyLoad: lazy ? lazyLoad : undefined,
    checkStrictly: lazy,
  };
}

export function getInstrumentDisplayName(instrument) {
  return instrument.name || instrument.latestVersion?.name || instrument.data?.[0]?.name;
}

export function getInstrumentValue(instrument, valueKey = 'name') {
  if(valueKey === 'name') return getInstrumentDisplayName(instrument);
  if(valueKey === 'latestVersion.name') return instrument.latestVersion?.name;
  if(valueKey === '_id') return instrument._id;
  return instrument[valueKey];
}

export async function getCascadeAreas({ axios, projectId, useUrl = null }) {
  const res = await axios({
    category: 'project',
    method: 'get',
    apiName: `${projectId}/areas`,
    useUrl,
  });
  return res.data.map(area => ({
    label: area.name,
    value: area.name,
    leaf: false,
  }));
}

export async function getCascadeStations({ axios, projectId, area, useUrl = null }) {
  const res = await axios({
    category: 'project',
    method: 'get',
    apiName: `${projectId}/areas/${encodeURIComponent(area)}/stations`,
    useUrl,
  });
  return res.data.map(station => ({
    label: station.name,
    value: station.name,
    leaf: false,
  }));
}

export async function getCascadeInstruments({
  axios,
  projectId,
  area,
  station,
  instrumentType,
  valueKey = 'name',
  useUrl = null,
}) {
  const res = await axios({
    category: 'project',
    method: 'get',
    apiName: `${projectId}/areas/${encodeURIComponent(area)}/stations/${encodeURIComponent(station)}/instruments`,
    useUrl,
  });
  return res.data
    .filter(instrument => {
      if(!instrumentType) return true;
      return String(instrument.instrument_type?._id) === String(instrumentType._id)
        || instrument.instrument_type?.name === instrumentType.name;
    })
    .map(instrument => {
      const label = getInstrumentDisplayName(instrument);
      const value = getInstrumentValue(instrument, valueKey);
      return {
        label,
        value,
        leaf: true,
      };
    })
    .filter(option => option.value);
}

export function createLazyLoadInstrumentOptions({ getAreas, getStations, getInstruments }) {
  return async function lazyLoadInstrumentOptions(node, resolve) {
    if(node.level === 0) {
      resolve(await getAreas());
      return;
    }

    if(node.level === 1) {
      resolve(await getStations(node.value));
      return;
    }

    if(node.level === 2) {
      const area = node.pathValues?.[0] || node.pathNodes?.[0]?.value || node.parent?.value;
      resolve(await getInstruments(area, node.value));
      return;
    }

    resolve([]);
  };
}

export function dedupeSelections(selectedList) {
  return selectedList.filter((selected, index, list) => {
    const key = selected.join('__');
    return list.findIndex(item => item.join('__') === key) === index;
  });
}

export async function expandSelectedCascadeToInstruments({
  selectedList,
  getStations,
  getInstruments,
}) {
  if(selectedList.length === 0) return selectedList;

  const expandedSelections = [];
  for(let selected of selectedList) {
    if(!selected?.[0]) continue;

    const area = selected[0];
    if(selected[2]) {
      expandedSelections.push(selected);
      continue;
    }

    const stations = selected[1]
      ? [{ value: selected[1] }]
      : await getStations(area);

    for(let station of stations) {
      const instruments = await getInstruments(area, station.value);
      instruments.forEach(instrument => {
        expandedSelections.push([area, station.value, instrument.value]);
      });
    }
  }

  return dedupeSelections(expandedSelections);
}

export function getSelectedAreas(selectedList) {
  return selectedList
    .map(selected => selected?.[0])
    .filter((area, index, list) => area && list.indexOf(area) === index);
}

export function getSelectedInstrumentValues(selectedList) {
  return selectedList
    .map(selected => selected?.[2])
    .filter((instrument, index, list) => instrument && list.indexOf(instrument) === index);
}
